import React, { useEffect, useMemo, useState } from "react";
import {
  Home, BarChart3, Briefcase, Users as UsersIcon, ListChecks,
  GitBranch, Clock, Timer, DollarSign, Truck, FileText, Search, Plus, LayoutDashboard,
  Layers, Wallet, PieChart, Loader2, X, Bot, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Paperclip,
  Trash2, Pencil, Check as CheckIcon
} from "lucide-react";
import { projectsApi, api } from "../../api";
import * as RC from "recharts";
import { useAuth } from "../../AuthContext";
import KanbanBoard from "../tasks/KanbanBoard";
import TaskDetail from "../tasks/TaskDetail";
import AnalyticsView from "../tasks/AnalyticsView";
import TasksChatbot from "../tasks/TasksChatbot";
import CreateTaskModal from "./CreateTaskModal";
import { avatarColorFor, initialsFor, COLUMNS } from "../tasks/constants";

/**
 * Unified Projects app: merges the previous "Projects" and "Tasks" apps.
 *  - Left rail: navigation (Home / Reports / Projects list / Overview…)
 *  - Right pane: selected project with tabs (Dashboard / Tasks / Users / Reports / Documents / Phases / Finance)
 *  - Tasks tab embeds the colorful KanbanBoard filtered to the current project.
 */
export default function ProjectsUnified() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null); // "__all__" | project.id | null
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [newForm, setNewForm] = useState({ name: "", description: "", status: "active", priority: "med", template: "blank", department: "" });
  const [projectSearch, setProjectSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const list = await projectsApi.list();
      setProjects(list || []);
      if (!selectedId) setSelectedId("__home__");
    } catch {
      setProjects([]);
      setSelectedId("__home__");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Load team member options (workspace users)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/directory");
        const list = (data || []).map(u => ({ address: u.address, name: u.name || u.username || u.address.split("@")[0] }));
        setTeamOptions(list);
      } catch { setTeamOptions([]); }
    })();
  }, []);

  // Department scoping — Admins (super admin, boss role, individual, or users
  // with no department set) see all. Everyone else sees only projects
  // matching their department.
  const isAdminLike = !!(user?.is_super_admin
    || ["boss", "individual", "admin"].includes(user?.role)
    || !user?.department);
  const scopedProjects = useMemo(() => {
    if (isAdminLike) return projects;
    const userDept = String(user?.department || "").toUpperCase().trim();
    return projects.filter(p => String(p.department || "").toUpperCase().trim() === userDept);
  }, [projects, isAdminLike, user]);

  const selected = useMemo(() => {
    if (selectedId === "__all__")    return { id: "__all__",    title: "All Projects", name: "All Projects", status: "active" };
    if (selectedId === "__issues__") return { id: "__issues__", title: "Issues",       name: "Issues",       status: "active" };
    const p = scopedProjects.find(x => x.id === selectedId);
    if (!p) return null;
    return { ...p, name: p.title || p.name };
  }, [scopedProjects, selectedId]);

  const filteredProjects = useMemo(() => {
    const s = projectSearch.trim().toLowerCase();
    if (!s) return scopedProjects;
    return scopedProjects.filter(p => ((p.title || p.name || "")).toLowerCase().includes(s));
  }, [scopedProjects, projectSearch]);

  const createProject = async () => {
    if (!newForm.name.trim()) return;
    try {
      const template = PROJECT_TEMPLATES[newForm.template] || null;
      const department = newForm.department || template?.department || "";
      const p = await projectsApi.create({
        title: newForm.name.trim(),
        description: newForm.description || template?.description || "",
        status: newForm.status,
        priority: newForm.priority,
        department,
        template: newForm.template,
      });
      setProjects(list => [p, ...list]);
      setSelectedId(p.id);
      // Seed default tasks from the chosen template (fire & forget — page will refresh on switch).
      if (template && template.tasks?.length) {
        try {
          const projName = p.title || p.name || newForm.name.trim();
          await Promise.all(template.tasks.map(t =>
            api.post("/tasks", {
              title: t.title,
              description: t.description || "",
              status: t.status || "todo",
              priority: t.priority || "medium",
              tags: t.tags || [department].filter(Boolean),
              project: projName,
              category_tag: t.category || "",
            }).catch(() => null)
          ));
        } catch (_) { /* ignore seeding failures */ }
      }
      setShowNew(false);
      setNewForm({ name: "", description: "", status: "active", priority: "med", template: "blank", department: "" });
    } catch (e) { alert(e?.response?.data?.detail || "Failed to create"); }
  };

  return (
    <div className="h-full w-full flex bg-slate-50 text-slate-800" data-testid="app-projects">
      {/* LEFT RAIL */}
      <aside className="w-60 shrink-0 bg-white border-r border-slate-200 flex flex-col">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-gradient-to-br from-indigo-500 to-cyan-500 text-white shadow-sm">
            <Briefcase size={14} />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.28em] text-indigo-500 mono">HEXA</div>
            <div className="text-sm font-bold text-slate-800 leading-tight">Projects</div>
          </div>
        </div>

        <div className="px-3 pt-3 pb-2">
          <button onClick={() => setShowNew(true)} data-testid="proj-new"
            className="w-full text-xs font-semibold px-3 py-2 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white flex items-center justify-center gap-1.5 shadow-sm">
            <Plus size={12} /> New Project
          </button>
        </div>

        <NavGroup title="">
          <NavItem icon={Home} label="Home" active={selectedId === "__home__"} onClick={() => setSelectedId("__home__")} />
          <NavItem icon={BarChart3} label="Reports" active={selectedId === "__reports__"} onClick={() => setSelectedId("__reports__")} />
          <NavItem icon={Briefcase} label="All Projects" active={selectedId === "__all__"} onClick={() => setSelectedId("__all__")} count={scopedProjects.length} />
        </NavGroup>

        <div className="px-4 py-2 flex items-center justify-between border-t border-slate-100 mt-2">
          <div className="text-[9px] uppercase tracking-[0.28em] text-slate-400 mono font-semibold">Projects</div>
          <span className="text-[10px] text-slate-400">{scopedProjects.length}</span>
        </div>
        <div className="px-3 pb-2 hexa-force-light">
          <div className="flex items-center gap-1 border-2 border-indigo-300 rounded px-2 py-1 text-xs bg-white">
            <Search size={11} className="text-indigo-500" />
            <input value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)}
                   placeholder="Search projects…" className="flex-1 outline-none text-xs font-semibold" data-testid="proj-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3">
          {loading && <div className="text-xs text-slate-400 px-3 py-2">Loading…</div>}
          {!loading && filteredProjects.length === 0 && (
            <div className="text-xs text-slate-400 px-3 py-2">No projects yet.</div>
          )}
          {/* Group projects by department with a colored department header */}
          {(() => {
            if (!filteredProjects.length) return null;
            const DEPT_META = {
              SALES:     { color: "#f43f5e", label: "Sales" },
              MARKETING: { color: "#8b5cf6", label: "Marketing" },
              IT:        { color: "#0ea5e9", label: "IT" },
              HR:        { color: "#f59e0b", label: "HR" },
              FINANCE:   { color: "#10b981", label: "Finance" },
              OTHER:     { color: "#64748b", label: "Other" },
            };
            const buckets = {};
            filteredProjects.forEach(p => {
              const raw = (p.department || "").toString().toUpperCase().trim() || "OTHER";
              const key = DEPT_META[raw] ? raw : "OTHER";
              (buckets[key] = buckets[key] || []).push(p);
            });
            const order = ["SALES", "MARKETING", "IT", "HR", "FINANCE", "OTHER"];
            return order.filter(k => buckets[k]).map(k => (
              <div key={k} className="mt-2" data-testid={`proj-dept-group-${k}`}>
                <div
                  className="px-3 py-1 flex items-center gap-2 text-[9px] font-extrabold uppercase tracking-[0.24em]"
                  style={{ color: DEPT_META[k].color }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: DEPT_META[k].color }} />
                  <span>{DEPT_META[k].label}</span>
                  <span className="ml-auto text-slate-400 font-normal">{buckets[k].length}</span>
                </div>
                {buckets[k].map(p => {
                  const active = selectedId === p.id;
                  const isNew = isCreatedToday(p.created || p.created_at || p.createdAt);
                  return (
                    <button key={p.id} onClick={() => setSelectedId(p.id)}
                      data-testid={`proj-item-${p.id}`}
                      className={`w-full text-left px-3 py-1.5 rounded flex items-center gap-2 text-xs transition ${active ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_DOT[p.status] || "#94a3b8" }} />
                      <span className="truncate flex-1">{p.title || p.name}</span>
                      {isNew && (
                        <span
                          data-testid={`proj-new-chip-${p.id}`}
                          className="text-[8px] font-extrabold tracking-widest px-1.5 py-0.5 rounded shadow-sm shrink-0"
                          style={{ background: "#22c55e", color: "#ffffff" }}
                          title="Created today"
                        >
                          NEW
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ));
          })()}
        </div>
      </aside>

      {/* RIGHT PANE */}
      <main className="flex-1 min-w-0 overflow-hidden flex flex-col">
        {selectedId === "__home__"       && <HomeOverview projects={scopedProjects} user={user} onNewProject={() => setShowNew(true)} onOpenProject={setSelectedId} />}
        {selectedId === "__reports__"    && <div className="flex-1 min-h-0 overflow-hidden"><AnalyticsView tasksCount={0} /></div>}
        {selectedId === "__phases__"     && <PhasesView />}
        {selectedId === "__timelogs__"   && <TimeLogsView />}
        {selectedId === "__timesheets__" && <TimesheetsView />}
        {selectedId === "__expenses__"   && <ExpensesView />}
        {selectedId === "__vendors__"    && <VendorsView />}
        {selected && !["__home__","__reports__","__phases__","__timelogs__","__timesheets__","__expenses__","__vendors__"].includes(selectedId) && (
          <ProjectDetail
            project={selected}
            isAll={selectedId === "__all__" || selectedId === "__issues__"}
            isIssues={selectedId === "__issues__"}
            user={user}
            onRefresh={load}
            projects={projects}
            teamOptions={teamOptions}
          />
        )}
      </main>

      {/* Floating AI Chat button */}
      <button onClick={() => setShowChatbot(true)} data-testid="proj-ai-chat"
        className="absolute bottom-4 right-4 z-20 rounded-full shadow-2xl text-white flex items-center gap-2 px-4 py-2.5 text-xs font-semibold"
        style={{ background: "linear-gradient(90deg, #6366f1 0%, #a855f7 60%, #d946ef 100%)" }}>
        <Bot size={14} /> Ask Novo AI
      </button>
      {showChatbot && <TasksChatbot onClose={() => setShowChatbot(false)} />}

      {/* New project modal */}
      {showNew && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-40" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-lg p-5 w-[560px] shadow-2xl space-y-3 hexa-force-light" onClick={(e) => e.stopPropagation()} data-testid="proj-new-modal">
            <div className="flex items-center justify-between">
              <div className="text-slate-800 font-semibold">New Project</div>
              <button onClick={() => setShowNew(false)} className="p-1 hover:bg-slate-100 rounded"><X size={14} /></button>
            </div>

            {/* Template picker */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Start from a template</div>
              <div className="grid grid-cols-5 gap-1.5">
                {Object.values(PROJECT_TEMPLATES).map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setNewForm(f => ({
                      ...f,
                      template: t.key,
                      department: t.department,
                      description: (!f.description || Object.values(PROJECT_TEMPLATES).some(x => x.description === f.description)) ? (t.description || "") : f.description,
                    }))}
                    data-testid={`proj-template-${t.key}`}
                    className="p-2 rounded border text-center transition"
                    style={{
                      borderColor: newForm.template === t.key ? t.color : "rgba(148,163,184,0.35)",
                      background:  newForm.template === t.key ? `${t.color}15` : "#fff",
                      color:       newForm.template === t.key ? t.color : "#334155",
                    }}
                  >
                    <div className="text-lg leading-none">{t.icon}</div>
                    <div className="text-[10px] font-semibold mt-1 uppercase tracking-wider">{t.label}</div>
                  </button>
                ))}
              </div>
              {PROJECT_TEMPLATES[newForm.template]?.tasks?.length > 0 && (
                <div className="mt-2 text-[10px] text-slate-500">
                  Will pre-seed <b>{PROJECT_TEMPLATES[newForm.template].tasks.length} starter tasks</b> tagged <span className="mono">{PROJECT_TEMPLATES[newForm.template].department}</span>.
                </div>
              )}
            </div>

            <input autoFocus placeholder="Project name" value={newForm.name}
              onChange={(e) => setNewForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm bg-white text-slate-800 placeholder:text-slate-400 outline-none focus:border-indigo-400"
              data-testid="proj-new-name" />
            <textarea placeholder="Description (optional)" value={newForm.description} rows={2}
              onChange={(e) => setNewForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-slate-200 rounded px-3 py-2 text-sm bg-white text-slate-800 placeholder:text-slate-400 outline-none focus:border-indigo-400 resize-none" />
            <div className="grid grid-cols-3 gap-2">
              <select value={newForm.status} onChange={(e) => setNewForm(f => ({ ...f, status: e.target.value }))}
                className="border border-slate-200 rounded px-2 py-1.5 text-xs bg-white text-slate-800">
                {Object.keys(STATUS_DOT).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={newForm.priority} onChange={(e) => setNewForm(f => ({ ...f, priority: e.target.value }))}
                className="border border-slate-200 rounded px-2 py-1.5 text-xs bg-white text-slate-800">
                {["low","med","high","urgent"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={newForm.department} onChange={(e) => setNewForm(f => ({ ...f, department: e.target.value }))}
                className="border border-slate-200 rounded px-2 py-1.5 text-xs bg-white text-slate-800" data-testid="proj-new-department">
                <option value="">No department</option>
                {DEPARTMENTS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="text-xs px-3 py-1.5 border border-slate-200 rounded text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={createProject} data-testid="proj-new-save"
                className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Project Detail — tabbed (Dashboard, Tasks, Users, Reports, Documents, Phases, Finance)
   ============================================================ */
function ProjectDetail({ project, isAll, isIssues = false, user, onRefresh, projects = [], teamOptions = [] }) {
  const [tab, setTab] = useState("tasks");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);

  const loadTasks = async () => {
    setLoading(true);
    try {
      // Issues view = fetch all tasks and filter client-side by priority/tags/category
      const q = (isAll || isIssues) ? "" : `?project=${encodeURIComponent(project.name)}`;
      const { data } = await api.get(`/tasks${q}`);
      setTasks(data || []);
    } catch { setTasks([]); }
    setLoading(false);
  };
  useEffect(() => { loadTasks(); }, [project?.id, project?.name]);

  const moveTo = async (id, status) => {
    setTasks(ts => ts.map(t => t.id === id ? { ...t, status } : t));
    try { await api.put(`/tasks/${id}`, { status }); } catch (_) { /* offline */ }
  };
  const openAdd = () => setShowAdd(true);
  const onTaskCreated = (data) => {
    if (data && data.id) {
      // Optimistic prepend so it appears instantly …
      setTasks(t => {
        if (t.some(x => x.id === data.id)) return t;
        return [data, ...t];
      });
    }
    setShowAdd(false);
    // … then reconcile with the backend so any server-side transforms
    // (task_code, timestamps, project field normalisation) are in sync.
    loadTasks();
  };
  const onTaskChange = (upd) => {
    if (!upd) return;
    setTasks(ts => ts.map(t => t.id === upd.id ? { ...t, ...upd } : t));
    setActiveTask(a => a && a.id === upd.id ? { ...a, ...upd } : a);
  };

  const enriched = useMemo(() => {
    // Build id/slug -> project name map so we can annotate each task with a
    // human-friendly project label when browsing "All Projects".
    const nameById = {};
    (projects || []).forEach(p => {
      if (p?.id) nameById[p.id] = p.name || p.title || "";
      if (p?.slug) nameById[p.slug] = p.name || p.title || "";
      if (p?.name) nameById[p.name] = p.name;
    });
    const base = tasks.map((t, i) => ({
      ...t,
      task_code: t.task_code || `Task-${100 + i + 1}`,
      project_name: nameById[t.project] || t.project || "",
      assignees: t.assignees?.length ? t.assignees : [{
        name: t.owner || user?.address || "?",
        initials: initialsFor(t.owner || user?.address || "?"),
        color: avatarColorFor(t.owner || user?.address || ""),
      }],
    }));
    if (isIssues) {
      // "Issue" = high/urgent priority OR tagged as issue/bug/blocker
      return base.filter(t => {
        const p = (t.priority || "").toLowerCase();
        if (p === "high" || p === "urgent") return true;
        const tags = (t.tags || []).map(x => String(x).toLowerCase());
        if (tags.some(x => /issue|bug|blocker|incident/.test(x))) return true;
        const cat = (t.category_tag || "").toLowerCase();
        if (/issue|bug|blocker|incident/.test(cat)) return true;
        return false;
      });
    }
    return base;
  }, [tasks, user, projects, isIssues]);

  const TABS = [
    { k: "dashboard",  label: "Dashboard",  icon: LayoutDashboard },
    { k: "tasks",      label: "Tasks",      icon: ListChecks },
    { k: "issues",     label: "Issues",     icon: GitBranch },
    { k: "calendar",   label: "Calendar",   icon: CalendarIcon },
    { k: "phases",     label: "Phases",     icon: Layers },
    { k: "timelogs",   label: "Time Logs",  icon: Clock },
    { k: "timesheets", label: "Timesheets", icon: Timer },
    { k: "expenses",   label: "Expenses",   icon: Wallet },
    { k: "vendors",    label: "Vendors",    icon: Truck },
    { k: "users",      label: "Users",      icon: UsersIcon },
    { k: "reports",    label: "Reports",    icon: PieChart },
    { k: "documents",  label: "Documents",  icon: FileText },
    { k: "finance",    label: "Finance",    icon: DollarSign },
  ];

  // Filter helper — same criteria as the sidebar Issues view.
  // Strict filter — a task counts as an "issue" only when it is
  // explicitly tagged / categorised as one. High-priority tasks are NOT
  // treated as issues.
  const isIssueTask = (t) => {
    const tags = (t.tags || []).map(x => String(x).toLowerCase());
    if (tags.some(x => /issue|bug|blocker|incident/.test(x))) return true;
    const cat = String(t.category_tag || t.category || "").toLowerCase();
    if (/issue|bug|blocker|incident/.test(cat)) return true;
    return false;
  };
  const issueTasks = useMemo(() => enriched.filter(isIssueTask), [enriched]);

  return (
    <>
      {/* Project header */}
      <div className="px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded mono uppercase tracking-widest">
            {project.code || (isAll ? "ALL" : `PR-${(project.id || "").slice(0, 3).toUpperCase()}`)}
          </div>
          <h1 className="text-xl font-bold text-slate-800">{project.name}</h1>
          <div className="flex items-center gap-1 ml-1">
            <span className="w-2 h-2 rounded-full" style={{ background: STATUS_DOT[project.status] || "#94a3b8" }} />
            <span className="text-[11px] uppercase tracking-widest text-slate-500 mono">{project.status || "active"}</span>
          </div>
          <div className="flex-1" />
          {tab === "tasks" && (
            <button onClick={() => openAdd("todo")} data-testid="proj-add-task"
              className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1.5 rounded hover:bg-indigo-700 flex items-center gap-1">
              <Plus size={12} /> Add Task
            </button>
          )}
          {tab === "issues" && (
            <button onClick={() => setShowIssueModal(true)} data-testid="proj-create-issue"
              className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1.5 rounded hover:bg-indigo-700 flex items-center gap-1">
              <Plus size={12} /> Create Issue
            </button>
          )}
        </div>
        <div className="mt-3 flex items-center gap-1 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon; const active = tab === t.k;
            return (
              <button key={t.k} onClick={() => setTab(t.k)}
                data-testid={`proj-tab-${t.k}`}
                className={`flex items-center gap-1.5 text-xs px-3 py-2 border-b-2 transition ${active ? "border-indigo-600 text-indigo-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <Icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden relative">
        {loading && (
          <div className="absolute top-3 right-3 z-10 flex items-center gap-2 text-xs text-slate-400 bg-white px-2 py-1 rounded shadow-sm">
            <Loader2 size={12} className="animate-spin" /> Loading…
          </div>
        )}
        {tab === "dashboard" && <ProjectDashboard project={project} tasks={enriched} isAll={isAll} projects={projects} user={user} />}
        {tab === "tasks"     && (
          <TasksView
            tasks={enriched}
            onOpen={setActiveTask}
            onDrop={moveTo}
            onAdd={openAdd}
            showProject={isAll}
            onOpenChatbot={() => setShowChatbot(true)}
          />
        )}
        {tab === "calendar"  && <TaskCalendarView tasks={enriched} onOpen={setActiveTask} showProject={isAll} />}
        {tab === "issues"    && (
          <IssuesView
            issues={issueTasks}
            onOpen={setActiveTask}
            onDrop={moveTo}
            onAdd={openAdd}
            showProject={isAll}
          />
        )}
        {tab === "users"     && <ProjectUsers tasks={enriched} />}
        {tab === "reports"   && <AnalyticsView tasksCount={enriched.length} />}
        {tab === "documents" && <EmptyState icon={FileText}  title="Documents" body="Attach spec docs, contracts, briefs and images to this project. Coming soon." />}
        {tab === "phases"    && <PhasesView projectFilter={isAll ? "" : project.name} />}
        {tab === "timelogs"  && <TimeLogsView projectFilter={isAll ? "" : project.name} />}
        {tab === "timesheets"&& <TimesheetsView projectFilter={isAll ? "" : project.name} />}
        {tab === "expenses"  && <ExpensesView projectFilter={isAll ? "" : project.name} />}
        {tab === "vendors"   && <VendorsView />}
        {tab === "finance"   && <EmptyState icon={DollarSign} title="Finance"  body="Track budget, actuals and per-task cost. Feeds into the CRM Finance dashboard. Coming soon." />}
      </div>

      {activeTask && (
        <TaskDetail
          task={enriched.find(t => t.id === activeTask.id) || activeTask}
          onClose={() => setActiveTask(null)}
          onChange={onTaskChange}
          currentUser={user}
        />
      )}

      <CreateTaskModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        projects={projects}
        defaultProject={isAll ? "Inbox" : (project.title || project.name)}
        onCreated={onTaskCreated}
        currentUser={user}
        teamOptions={teamOptions}
      />

      {showIssueModal && (
        <SubmitIssueModal
          onClose={() => setShowIssueModal(false)}
          onCreated={() => { setShowIssueModal(false); loadTasks(); }}
          allTasks={enriched}
          defaultProject={isAll ? "" : (project.title || project.name)}
        />
      )}

      {showChatbot && <TasksChatbot onClose={() => setShowChatbot(false)} />}
    </>
  );
}

/* ============================================================
   Small subviews
   ============================================================ */
function ProjectDashboard({ project, tasks, isAll, projects = [], user }) {
  const total = tasks.length;
  const done = tasks.filter(t => t.status === "done" || t.status === "completed").length;
  const progress = tasks.filter(t => t.status === "in_progress" || t.status === "doing").length;
  const onhold = tasks.filter(t => t.status === "on_hold").length;
  const completion = total ? Math.round((done / total) * 100) : 0;

  const role = (user?.role || "").toLowerCase();
  const isBoss = !!user && (
    user.is_super_admin ||
    ["boss", "admin", "owner", "director"].includes(role) ||
    user.account_type === "business"
  );

  const KPIs = [
    { label: "Total Tasks",  value: total,    tint: "#6366f1" },
    { label: "Completed",    value: done,     tint: "#22c55e" },
    { label: "In Progress",  value: progress, tint: "#3b82f6" },
    { label: "On Hold",      value: onhold,   tint: "#c026d3" },
  ];

  // ---- Widget datasets ------------------------------------------------------
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday   = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000 - 1);
  const endOfWeek    = new Date(startOfToday.getTime() + 7  * 24 * 60 * 60 * 1000);
  const endOfMonthAhead = new Date(startOfToday.getFullYear(), startOfToday.getMonth() + 1, startOfToday.getDate());

  const parseDate = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt;
  };
  const isCompleted = (t) => t.status === "done" || t.status === "completed";

  // 1) Total tasks by section
  const sectionData = (() => {
    const buckets = { Recently: 0, "Do today": 0, "Do next": 0, "Do later": 0 };
    tasks.forEach(t => {
      const upd = parseDate(t.updated || t.updated_at) || parseDate(t.created);
      const due = parseDate(t.due || t.end_date);
      // Recently: updated within the last 3 days AND not overdue
      if (upd && (now - upd) < 3 * 24 * 60 * 60 * 1000) { buckets.Recently += 1; return; }
      if (!due) { buckets["Do later"] += 1; return; }
      if (due >= startOfToday && due <= endOfToday) buckets["Do today"] += 1;
      else if (due > endOfToday && due <= endOfWeek) buckets["Do next"] += 1;
      else buckets["Do later"] += 1;
    });
    return Object.entries(buckets).map(([name, value]) => ({ name, value }));
  })();

  // 2) Tasks by completion status this upcoming month (donut)
  const monthTasks = tasks.filter(t => {
    const due = parseDate(t.due || t.end_date);
    return due && due >= startOfToday && due <= endOfMonthAhead;
  });
  const monthComplete = monthTasks.filter(isCompleted).length;
  const monthIncomplete = monthTasks.length - monthComplete;
  const completionStatusData = [
    { name: "Incomplete", value: monthIncomplete, color: "#a78bfa" },
    { name: "Complete",   value: monthComplete,   color: "#22c55e" },
  ].filter(d => d.value > 0);

  // 3) Total tasks by project
  const projectData = (() => {
    const map = {};
    tasks.forEach(t => {
      const key = t.project_name || t.project || "Inbox";
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, value]) => ({ name, value }));
  })();

  // 4) Task completion over time (past 14 days) — cumulative Total vs Completed
  const timeSeries = (() => {
    const days = 14;
    const start = new Date(startOfToday.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
    const rows = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
      const eod = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
      const totalUntil = tasks.filter(t => {
        const c = parseDate(t.created); return c && c <= eod;
      }).length;
      const completedUntil = tasks.filter(t => {
        if (!isCompleted(t)) return false;
        const cAt = parseDate(t.completed_at || t.updated || t.updated_at || t.created);
        return cAt && cAt <= eod;
      }).length;
      const label = `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
      rows.push({ name: label, Total: totalUntil, Completed: completedUntil });
    }
    return rows;
  })();

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="max-w-6xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {KPIs.map(k => (
            <div key={k.label} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mono">{k.label}</div>
              <div className="text-2xl font-bold mt-2" style={{ color: k.tint }}>{k.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mono">Completion Rate</div>
          <div className="flex items-baseline justify-between mt-2">
            <div className="text-slate-600 text-sm font-semibold">{isAll ? "All Projects" : project.name}</div>
            <div className="text-3xl font-extrabold text-indigo-600">{completion}%</div>
          </div>
          <div className="mt-3 h-3 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all" style={{ width: `${completion}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span>{done} completed</span><span>{total - done} remaining</span>
          </div>
        </div>

        {/* Analytics widgets — mirrors the reference layout */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DashboardWidget title="Total tasks by section" testId="widget-tasks-by-section">
            <RC.ResponsiveContainer width="100%" height={220}>
              <RC.BarChart data={sectionData} margin={{ top: 20, right: 20, left: 0, bottom: 32 }}>
                <RC.CartesianGrid vertical={false} stroke="#eef2f7" />
                <RC.XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} angle={-25} textAnchor="end" interval={0} />
                <RC.YAxis tick={{ fontSize: 11, fill: "#64748b" }} label={{ value: "Task count", angle: -90, position: "insideLeft", offset: 12, style: { fill: "#94a3b8", fontSize: 11 } }} allowDecimals={false} />
                <RC.Tooltip cursor={{ fill: "rgba(99,102,241,0.06)" }} />
                <RC.Bar dataKey="value" fill="#a78bfa" radius={[3, 3, 0, 0]} maxBarSize={40}>
                  <RC.LabelList dataKey="value" position="top" style={{ fill: "#475569", fontSize: 11 }} />
                </RC.Bar>
              </RC.BarChart>
            </RC.ResponsiveContainer>
          </DashboardWidget>

          <DashboardWidget title="Tasks by completion status this upcoming month" testId="widget-completion-status">
            {completionStatusData.length === 0 ? (
              <div className="h-[220px] flex items-center justify-center text-xs text-slate-400">No tasks due this month</div>
            ) : (
              <RC.ResponsiveContainer width="100%" height={220}>
                <RC.PieChart>
                  <RC.Pie data={completionStatusData} dataKey="value" nameKey="name" cx="45%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={2}>
                    {completionStatusData.map((d, i) => <RC.Cell key={i} fill={d.color} />)}
                    <RC.Label value={String(completionStatusData.reduce((s, d) => s + d.value, 0))} position="center" style={{ fill: "#1e293b", fontSize: 22, fontWeight: 800 }} />
                  </RC.Pie>
                  <RC.Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: 11 }} />
                  <RC.Tooltip />
                </RC.PieChart>
              </RC.ResponsiveContainer>
            )}
          </DashboardWidget>

          <DashboardWidget title="Total tasks by project" testId="widget-tasks-by-project">
            {projectData.length === 0 ? (
              <div className="h-[220px] flex items-center justify-center text-xs text-slate-400">No tasks</div>
            ) : (
              <RC.ResponsiveContainer width="100%" height={220}>
                <RC.BarChart data={projectData} margin={{ top: 20, right: 20, left: 0, bottom: 42 }}>
                  <RC.CartesianGrid vertical={false} stroke="#eef2f7" />
                  <RC.XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} angle={-30} textAnchor="end" interval={0} height={50} />
                  <RC.YAxis tick={{ fontSize: 11, fill: "#64748b" }} label={{ value: "Task count", angle: -90, position: "insideLeft", offset: 12, style: { fill: "#94a3b8", fontSize: 11 } }} allowDecimals={false} />
                  <RC.Tooltip cursor={{ fill: "rgba(99,102,241,0.06)" }} />
                  <RC.Bar dataKey="value" fill="#a78bfa" radius={[3, 3, 0, 0]} maxBarSize={40}>
                    <RC.LabelList dataKey="value" position="top" style={{ fill: "#475569", fontSize: 11 }} />
                  </RC.Bar>
                </RC.BarChart>
              </RC.ResponsiveContainer>
            )}
          </DashboardWidget>

          <DashboardWidget title="Task completion over time" testId="widget-completion-over-time">
            <RC.ResponsiveContainer width="100%" height={220}>
              <RC.AreaChart data={timeSeries} margin={{ top: 10, right: 20, left: 0, bottom: 24 }}>
                <defs>
                  <linearGradient id="pd-total" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#a78bfa" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="pd-done" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6d28d9" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#6d28d9" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <RC.CartesianGrid vertical={false} stroke="#eef2f7" />
                <RC.XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={1} angle={-20} textAnchor="end" />
                <RC.YAxis tick={{ fontSize: 11, fill: "#64748b" }} label={{ value: "Task count", angle: -90, position: "insideLeft", offset: 12, style: { fill: "#94a3b8", fontSize: 11 } }} allowDecimals={false} />
                <RC.Tooltip />
                <RC.Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <RC.Area type="monotone" dataKey="Total"     stroke="#a78bfa" strokeWidth={2} fill="url(#pd-total)" />
                <RC.Area type="monotone" dataKey="Completed" stroke="#6d28d9" strokeWidth={2} fill="url(#pd-done)" />
              </RC.AreaChart>
            </RC.ResponsiveContainer>
          </DashboardWidget>
        </div>

        {/* All Projects only — People + My Tasks + Project overview table */}
        {isAll && (
          <>
            {isBoss && <DepartmentAnalytics projects={projects} tasks={tasks} />}
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PeopleWidget tasks={tasks} />
              <MyTasksWidget tasks={tasks} user={user} />
            </div>
            <ProjectsOverviewTable projects={projects} tasks={tasks} />
          </>
        )}
      </div>
    </div>
  );
}

function DashboardWidget({ title, testId, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid={testId}>
      <div className="text-sm font-semibold text-slate-800 mb-2">{title}</div>
      <div>{children}</div>
    </div>
  );
}

/* ============================================================
   DepartmentAnalytics — Boss / Admin only.
   Shows a compact stat card per department (Sales, Marketing, IT, HR)
   with task counts, completion %, budget/hours summaries.
   Departments are inferred from:
     - project.department (if the project was made from a template)
     - task.tags containing the department key
     - task.category_tag hint
   ============================================================ */
function DepartmentAnalytics({ projects = [], tasks = [] }) {
  const isDone = (t) => t.status === "done" || t.status === "completed";
  const isInProgress = (t) => t.status === "in_progress" || t.status === "doing";

  const belongsTo = (t, deptKey, projectDeptByName) => {
    // Direct department tag on the task.
    const tags = (t.tags || []).map(x => String(x).toLowerCase());
    if (tags.includes(deptKey)) return true;
    // Or via its project's department.
    const projDept = projectDeptByName[(t.project_name || t.project || "").toLowerCase()];
    if (projDept === deptKey) return true;
    // category_tag hint (uppercase words that match dept keywords).
    const cat = (t.category_tag || "").toLowerCase();
    if (cat.includes(deptKey)) return true;
    return false;
  };

  const projectDeptByName = useMemo(() => {
    const m = {};
    (projects || []).forEach(p => {
      const key = (p.name || p.title || "").toLowerCase();
      if (key) m[key] = (p.department || "").toLowerCase();
    });
    return m;
  }, [projects]);

  const stats = DEPARTMENTS.map(d => {
    const deptTasks = tasks.filter(t => belongsTo(t, d.key, projectDeptByName));
    const deptProjects = (projects || []).filter(p => (p.department || "").toLowerCase() === d.key);
    const total = deptTasks.length;
    const done = deptTasks.filter(isDone).length;
    const inProg = deptTasks.filter(isInProgress).length;
    const overdue = deptTasks.filter(t => {
      if (isDone(t)) return false;
      const due = t.due || t.end_date; if (!due) return false;
      const dt = new Date(due); if (isNaN(dt.getTime())) return false;
      return dt < new Date();
    }).length;
    const hours = deptTasks.reduce((s, t) => s + (t.work_hours || 0), 0);
    const completion = total ? Math.round((done / total) * 100) : 0;
    return { ...d, projects: deptProjects.length, total, done, inProg, overdue, hours, completion };
  });

  return (
    <div className="mt-5 bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="dept-analytics">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-lg font-bold text-slate-800">Departments overview</div>
          <div className="text-xs text-slate-500">Only visible to Boss / Admin — real-time analytics across Sales, Marketing, IT and HR.</div>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-indigo-50 text-indigo-600">Admin only</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {stats.map(s => (
          <div key={s.key} className="rounded-lg border border-slate-200 overflow-hidden bg-white" data-testid={`dept-card-${s.key}`}>
            <div className="px-4 py-2.5 flex items-center justify-between text-white hexa-on-accent" style={{ background: s.color }}>
              <div className="flex items-center gap-2 font-bold text-sm"><span className="text-base">{s.icon}</span>{s.label}</div>
              <div className="text-xs font-mono bg-white/25 rounded px-1.5 py-0.5">{s.projects} proj</div>
            </div>
            <div className="p-4">
              <div className="flex items-baseline gap-3">
                <div className="text-3xl font-extrabold text-slate-800">{s.total}</div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">tasks</div>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${s.completion}%`, background: s.color }} />
              </div>
              <div className="mt-1 text-[10px] font-mono text-slate-500 flex items-center justify-between">
                <span>{s.completion}% complete</span>
                <span>{s.hours.toFixed(0)}h logged</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-1 text-center">
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Done</div>
                  <div className="text-sm font-bold text-emerald-600">{s.done}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">In-Prog</div>
                  <div className="text-sm font-bold text-blue-600">{s.inProg}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Overdue</div>
                  <div className="text-sm font-bold text-rose-500">{s.overdue}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   Utility — resolve raw email/handle to a display name.
   ============================================================ */
function displayName(raw) {
  if (!raw) return "";
  let s = String(raw).trim();
  if (s.startsWith("@")) s = s.slice(1);
  if (s.includes("@")) s = s.split("@")[0];
  if (s.toLowerCase() === "info") return "HEXA Admin";
  return s.split(/[._\-\s]+/).filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}

/* ============================================================
   PeopleWidget — team members with overdue / completed / upcoming counts.
   ============================================================ */
function PeopleWidget({ tasks }) {
  const now = new Date();
  const people = useMemo(() => {
    const map = new Map();
    tasks.forEach(t => {
      const key = t.owner || "unknown";
      if (!map.has(key)) map.set(key, { key, name: displayName(key), overdue: 0, completed: 0, upcoming: 0 });
      const rec = map.get(key);
      const isDone = t.status === "done" || t.status === "completed";
      const due = t.due || t.end_date;
      const dueDate = due ? new Date(due) : null;
      if (isDone) rec.completed += 1;
      else if (dueDate && !isNaN(dueDate.getTime()) && dueDate < now) rec.overdue += 1;
      else rec.upcoming += 1;
    });
    return Array.from(map.values()).sort((a, b) => (b.overdue + b.upcoming) - (a.overdue + a.upcoming));
  }, [tasks]);

  const initials = (n) => (n || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  const tint = (n) => { let h = 0; for (const c of (n || "?")) h = (h * 31 + c.charCodeAt(0)) % 360; return `hsl(${h} 70% 55%)`; };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="widget-people">
      <div className="text-lg font-bold text-slate-800">People</div>
      <div className="text-xs text-slate-500 mt-0.5">See who&apos;s on track and who needs support at a glance.</div>

      <div className="mt-4 divide-y divide-slate-100">
        {people.length === 0 ? (
          <div className="text-xs text-slate-400 py-6 text-center">No team members yet.</div>
        ) : people.slice(0, 6).map(p => (
          <div key={p.key} className="flex items-center gap-3 py-2.5" data-testid={`people-row-${p.key}`}>
            <div className="w-7 h-7 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0" style={{ background: tint(p.name) }}>{initials(p.name)}</div>
            <div className="flex-1 min-w-0 text-sm font-semibold text-slate-800 truncate">{p.name}</div>
            <div className="flex items-center gap-3 text-[11px] shrink-0">
              <span className="text-rose-500 font-semibold">{p.overdue} overdue</span>
              <span className="text-emerald-600 font-semibold">{p.completed} completed</span>
              <span className="text-indigo-500 font-semibold">{p.upcoming} upcoming</span>
            </div>
          </div>
        ))}
      </div>

      <button className="mt-4 text-xs font-semibold px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700" data-testid="people-invite-btn">Invite a teammate</button>
    </div>
  );
}

/* ============================================================
   MyTasksWidget — current user's tasks split by Upcoming/Overdue/Completed.
   ============================================================ */
function MyTasksWidget({ tasks, user }) {
  const [tab, setTab] = useState("upcoming");
  const me = user?.address || "";
  const now = new Date();

  const mine = tasks.filter(t => (t.owner || "") === me || ((t.assignees || []).some(a => (a.email || a.address) === me)));
  const isDone = (t) => t.status === "done" || t.status === "completed";
  const parseDue = (t) => {
    const raw = t.due || t.end_date; if (!raw) return null;
    const d = new Date(raw); return isNaN(d.getTime()) ? null : d;
  };
  const overdue   = mine.filter(t => !isDone(t) && parseDue(t) && parseDue(t) < now);
  const upcoming  = mine.filter(t => !isDone(t) && !overdue.includes(t));
  const completed = mine.filter(isDone);
  const active = tab === "upcoming" ? upcoming : tab === "overdue" ? overdue : completed;

  const meName = displayName(me);
  const initials = meName.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  const tint = (n) => { let h = 0; for (const c of (n || "?")) h = (h * 31 + c.charCodeAt(0)) % 360; return `hsl(${h} 70% 55%)`; };

  const formatDate = (d) => {
    if (!d) return "";
    const mon = d.toLocaleString("en-US", { month: "short" });
    return `${mon} ${d.getDate()}`;
  };
  const projectPillColor = (name) => { let h = 0; for (const c of (name || "?")) h = (h * 31 + c.charCodeAt(0)) % 360; return `hsl(${h} 65% 60%)`; };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="widget-my-tasks">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full text-white text-sm font-bold flex items-center justify-center shrink-0" style={{ background: tint(meName) }}>{initials || "ME"}</div>
        <div className="text-lg font-bold text-slate-800">My tasks</div>
      </div>

      <div className="mt-3 flex items-center gap-4 border-b border-slate-200">
        <TabBtn active={tab === "upcoming"}  onClick={() => setTab("upcoming")}  label="Upcoming" count={upcoming.length}   testId="mytasks-tab-upcoming" />
        <TabBtn active={tab === "overdue"}   onClick={() => setTab("overdue")}   label="Overdue"  count={overdue.length}    testId="mytasks-tab-overdue" />
        <TabBtn active={tab === "completed"} onClick={() => setTab("completed")} label="Completed" count={completed.length} testId="mytasks-tab-completed" />
      </div>

      <div className="mt-3 max-h-72 overflow-y-auto">
        <button className="w-full flex items-center gap-2 text-sm text-indigo-600 py-2 hover:bg-indigo-50 rounded px-2 font-semibold" data-testid="mytasks-create-btn">
          <Plus size={13} /> Create task
        </button>
        {active.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-6">No tasks here yet.</div>
        ) : active.slice(0, 20).map(t => {
          const due = parseDue(t);
          const proj = t.project_name || t.project || "";
          return (
            <div key={t.id} className="flex items-center gap-2 px-2 py-2 border-b border-slate-100 hover:bg-slate-50" data-testid={`mytasks-item-${t.id}`}>
              <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" style={{ background: isDone(t) ? "#22c55e" : "transparent", borderColor: isDone(t) ? "#22c55e" : "#cbd5e1" }} />
              <div className="flex-1 min-w-0 text-sm text-slate-700 truncate">{t.title || "Untitled"}</div>
              {proj && (
                <span className="text-[10px] font-semibold text-white rounded px-1.5 py-0.5 shrink-0 max-w-[100px] truncate" style={{ background: projectPillColor(proj) }} title={proj}>{proj}</span>
              )}
              {due && <span className="text-[11px] text-slate-500 tabular-nums shrink-0">{formatDate(due)}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, label, count, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`pb-2 text-sm border-b-2 transition ${active ? "text-slate-900 font-bold border-slate-900" : "text-slate-500 border-transparent hover:text-slate-700"}`}
    >
      {label}{count > 0 ? ` (${count})` : ""}
    </button>
  );
}

/* ============================================================
   ProjectsOverviewTable — big table across all projects.
   ============================================================ */
function ProjectsOverviewTable({ projects = [], tasks = [] }) {
  const rows = useMemo(() => projects.map(p => {
    const pName = p.name || p.title || "";
    const pTasks = tasks.filter(t => (t.project_name || t.project) === pName);
    const totalHours = pTasks.reduce((s, t) => s + (t.work_hours || 0), 0);
    const completedHours = pTasks.filter(t => t.status === "done" || t.status === "completed").reduce((s, t) => s + (t.work_hours || 0), 0);
    const total = pTasks.length;
    const yetToStart = pTasks.filter(t => !t.status || t.status === "backlog" || t.status === "todo" || t.status === "to_do").length;
    const progress = pTasks.filter(t => t.status === "in_progress" || t.status === "doing").length;
    const completed = pTasks.filter(t => t.status === "done" || t.status === "completed").length;
    const now = new Date();
    const due = pTasks.filter(t => {
      const d = t.due || t.end_date; if (!d) return false;
      const dt = new Date(d); if (isNaN(dt.getTime())) return false;
      return dt < now && !(t.status === "done" || t.status === "completed");
    }).length;
    const team = new Set(pTasks.map(t => t.owner).filter(Boolean));
    const teamOwners = Array.from(team).slice(0, 3);
    const planned = p.planned_tasks || p.plan_count || total;
    return {
      id: p.id || pName,
      name: pName || "—",
      manager: displayName(p.owner || p.manager || (pTasks[0]?.owner) || ""),
      managerRaw: p.owner || p.manager || pTasks[0]?.owner || "",
      teamCount: team.size,
      teamAvatars: teamOwners.map(displayName),
      startDate: p.start_date || (pTasks[0] && pTasks[0].created) || "",
      endDate:   p.end_date   || (pTasks[0] && pTasks[0].due) || "",
      totalHours, completedHours, total, yetToStart, progress, completed, due, planned,
    };
  }), [projects, tasks]);

  const initials = (n) => (n || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  const tint = (n) => { let h = 0; for (const c of (n || "?")) h = (h * 31 + c.charCodeAt(0)) % 360; return `hsl(${h} 70% 55%)`; };
  const fmt = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw); if (isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  };

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="widget-projects-overview">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gradient-to-r from-indigo-50 to-sky-50 text-slate-700">
              <th className="px-3 py-2 text-left font-bold" rowSpan={2}>Project Name</th>
              <th className="px-3 py-2 text-left font-bold" rowSpan={2}>Project Manager</th>
              <th className="px-3 py-2 text-left font-bold" rowSpan={2}>Team</th>
              <th className="px-3 py-2 text-center font-bold border-l border-slate-200" colSpan={2}>Date</th>
              <th className="px-3 py-2 text-center font-bold border-l border-slate-200" colSpan={7}>Task</th>
            </tr>
            <tr className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
              <th className="px-3 py-1.5 text-left font-semibold border-l border-slate-200">Start Date</th>
              <th className="px-3 py-1.5 text-left font-semibold">End Date</th>
              <th className="px-3 py-1.5 text-left font-semibold border-l border-slate-200">Total Hours</th>
              <th className="px-3 py-1.5 text-left font-semibold">Completed Hours</th>
              <th className="px-3 py-1.5 text-left font-semibold">Total</th>
              <th className="px-3 py-1.5 text-left font-semibold">Yet To Start</th>
              <th className="px-3 py-1.5 text-left font-semibold">Progress</th>
              <th className="px-3 py-1.5 text-left font-semibold">Completed</th>
              <th className="px-3 py-1.5 text-left font-semibold">Due</th>
              <th className="px-3 py-1.5 text-left font-semibold">Planned</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400 text-xs">No projects yet — create one from the sidebar.</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`projects-overview-row-${r.id}`}>
                <td className="px-3 py-2.5 font-semibold text-slate-800 whitespace-nowrap">{r.name}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center -space-x-2">
                    {r.teamAvatars.length === 0 && r.manager ? (
                      <div className="w-6 h-6 rounded-full text-white text-[9px] font-bold flex items-center justify-center" style={{ background: tint(r.manager) }}>{initials(r.manager)}</div>
                    ) : r.teamAvatars.map((n, i) => (
                      <div key={i} className="w-6 h-6 rounded-full text-white text-[9px] font-bold flex items-center justify-center border-2 border-white" style={{ background: tint(n) }} title={n}>{initials(n)}</div>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-slate-700 font-semibold">{r.teamCount}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmt(r.startDate)}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmt(r.endDate)}</td>
                <td className="px-3 py-2.5 text-slate-700 font-semibold">{r.totalHours.toFixed(0)}</td>
                <td className="px-3 py-2.5 text-emerald-600 font-semibold">{r.completedHours.toFixed(0)}</td>
                <td className="px-3 py-2.5 text-slate-700 font-semibold">{r.total}</td>
                <td className="px-3 py-2.5 text-slate-500 font-semibold">{r.yetToStart}</td>
                <td className="px-3 py-2.5 text-blue-600 font-semibold">{r.progress}</td>
                <td className="px-3 py-2.5 text-emerald-600 font-semibold">{r.completed}</td>
                <td className="px-3 py-2.5 text-rose-500 font-semibold">{r.due}</td>
                <td className="px-3 py-2.5 text-indigo-600 font-semibold">{r.planned}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================
   TaskCalendarView — Month + Week grid of tasks by their due date.
   ============================================================ */
function TaskCalendarView({ tasks, onOpen, showProject = false }) {
  const today = new Date();
  const [view, setView] = useState("month");                  // "month" | "week"
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [weekCursor, setWeekCursor] = useState(startOfWeek(today));

  function startOfWeek(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - x.getDay()); // roll back to Sunday
    return x;
  }

  // Month grid cells
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const last  = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startWeekday = first.getDay();
  const daysInMonth = last.getDate();
  const monthCells = [];
  for (let i = 0; i < startWeekday; i++) monthCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) monthCells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  while (monthCells.length % 7 !== 0) monthCells.push(null);
  while (monthCells.length < 42) monthCells.push(null);

  // Week grid cells — 7 consecutive dates from weekCursor
  const weekCells = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekCursor);
    d.setDate(d.getDate() + i);
    return d;
  });

  // Bucket tasks by date key YYYY-MM-DD
  const bucketed = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      const raw = t.due || t.end_date;
      if (!raw) return;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      (map[key] = map[key] || []).push(t);
    });
    return map;
  }, [tasks]);

  const monthName = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });
  const weekLabel = (() => {
    const end = new Date(weekCursor); end.setDate(end.getDate() + 6);
    const same = weekCursor.getMonth() === end.getMonth();
    const fmt = (d, opts) => d.toLocaleString("en-US", opts);
    return same
      ? `${fmt(weekCursor, { month: "short" })} ${weekCursor.getDate()} – ${end.getDate()}, ${end.getFullYear()}`
      : `${fmt(weekCursor, { month: "short", day: "numeric" })} – ${fmt(end, { month: "short", day: "numeric", year: "numeric" })}`;
  })();

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
  const monthMatches = (d) => d && d.getMonth() === cursor.getMonth();

  const nav = (delta) => {
    if (view === "month") setCursor(c => new Date(c.getFullYear(), c.getMonth() + delta, 1));
    else setWeekCursor(w => { const n = new Date(w); n.setDate(n.getDate() + delta * 7); return n; });
  };
  const goToday = () => {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setWeekCursor(startOfWeek(today));
  };

  const statusColor = (t) => {
    if (t.status === "done" || t.status === "completed") return "#22c55e";
    if (t.status === "in_progress" || t.status === "doing") return "#3b82f6";
    if (t.status === "on_hold") return "#c026d3";
    if ((t.priority || "").toLowerCase() === "high" || (t.priority || "").toLowerCase() === "urgent") return "#ef4444";
    return "#6366f1";
  };

  const gridCells = view === "month" ? monthCells : weekCells;
  const cellMinHeight = view === "month" ? "min-h-[110px]" : "min-h-[340px]";
  const previewLimit = view === "month" ? 3 : 12;

  return (
    <div className="p-4 overflow-y-auto h-full bg-slate-50">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button onClick={() => nav(-1)} data-testid="cal-prev-btn" className="p-1.5 rounded border-2 border-indigo-300 hover:bg-indigo-50 text-indigo-600 bg-white font-bold"><ChevronLeft size={14} /></button>
        <button onClick={goToday}      data-testid="cal-today-btn" className="px-3 py-1 rounded border-2 border-indigo-300 hover:bg-indigo-50 text-xs font-bold text-indigo-700 bg-white uppercase tracking-wider">Today</button>
        <button onClick={() => nav(1)}  data-testid="cal-next-btn" className="p-1.5 rounded border-2 border-indigo-300 hover:bg-indigo-50 text-indigo-600 bg-white font-bold"><ChevronRight size={14} /></button>
        <div className="text-base font-bold text-slate-800 ml-2" data-testid="cal-range-label">
          {view === "month" ? monthName : weekLabel}
        </div>
        <div className="flex-1" />

        {/* Week / Month toggle */}
        <div className="flex items-center gap-0 border border-slate-200 rounded overflow-hidden bg-white">
          <button
            onClick={() => setView("week")}
            data-testid="cal-view-week"
            className={`text-xs font-semibold px-3 py-1 flex items-center gap-1 transition ${view === "week" ? "bg-indigo-500 text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <CalendarIcon size={12} /> Week
          </button>
          <button
            onClick={() => setView("month")}
            data-testid="cal-view-month"
            className={`text-xs font-semibold px-3 py-1 flex items-center gap-1 transition ${view === "month" ? "bg-indigo-500 text-white" : "text-slate-600 hover:bg-slate-50"}`}
          >
            <LayoutDashboard size={12} /> Month
          </button>
        </div>
        <div className="text-[11px] text-slate-500">
          {Object.values(bucketed).reduce((s, arr) => s + arr.length, 0)} tasks scheduled
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d, idx) => {
          const dayColors = ["#ef4444","#f97316","#f59e0b","#10b981","#3b82f6","#6366f1","#ec4899"];
          return (
            <div
              key={d}
              className="bg-white text-center text-[10px] uppercase tracking-widest font-bold py-2 border-b-2"
              style={{ color: dayColors[idx], borderColor: dayColors[idx] }}
            >
              {d}
            </div>
          );
        })}
        {gridCells.map((d, i) => {
          if (!d) return <div key={i} className={`bg-slate-50 ${cellMinHeight}`} />;
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const list = bucketed[key] || [];
          const isToday = isSameDay(d, today);
          const inScope = view === "month" ? monthMatches(d) : true;
          return (
            <div
              key={i}
              className={`bg-white ${cellMinHeight} p-1.5 flex flex-col gap-1 ${inScope ? "" : "opacity-50"}`}
              data-testid={`cal-cell-${key}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[11px] font-semibold ${isToday ? "bg-indigo-600 text-white rounded-full px-1.5 py-0.5" : "text-slate-700"}`}>{d.getDate()}</span>
                {list.length > 0 && !isToday && (
                  <span className="text-[9px] text-slate-400 font-mono">{list.length}</span>
                )}
              </div>
              <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                {list.slice(0, previewLimit).map(t => (
                  <button
                    key={t.id}
                    onClick={() => onOpen?.(t)}
                    data-testid={`cal-task-${t.id}`}
                    className="text-left text-[10px] truncate px-1.5 py-0.5 rounded font-semibold hover:brightness-95 transition"
                    style={{ background: `${statusColor(t)}22`, color: statusColor(t), borderLeft: `2px solid ${statusColor(t)}` }}
                    title={`${t.title || "Task"}${showProject && t.project_name ? " · " + t.project_name : ""}`}
                  >
                    {t.title || "Untitled"}{showProject && t.project_name ? ` · ${t.project_name}` : ""}
                  </button>
                ))}
                {list.length > previewLimit && (
                  <div className="text-[9px] text-slate-500 pl-1">+{list.length - previewLimit} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] text-slate-500">
        <LegendDot color="#6366f1" label="To Do" />
        <LegendDot color="#3b82f6" label="In Progress" />
        <LegendDot color="#c026d3" label="On Hold" />
        <LegendDot color="#22c55e" label="Done" />
        <LegendDot color="#ef4444" label="High / Urgent" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </span>
  );
}

/* ============================================================
   TasksView — Tasks tab with List + Board toggle (mirrors IssuesView).
   ============================================================ */

const TASK_STATUS_META = {
  todo:         { label: "To do",        color: "#2563eb", bg: "#3b82f6" },
  yet_to_start: { label: "Yet to start", color: "#64748b", bg: "#94a3b8" },
  in_progress: { label: "In progress",  color: "#f59e0b", bg: "#f59e0b" },
  doing:       { label: "In progress",  color: "#f59e0b", bg: "#f59e0b" },
  on_hold:     { label: "On hold",      color: "#c026d3", bg: "#c026d3" },
  done:        { label: "Completed",    color: "#0ea5e9", bg: "#22d3ee" },
  completed:   { label: "Completed",    color: "#0ea5e9", bg: "#22d3ee" },
  backlog:     { label: "Backlog",      color: "#64748b", bg: "#94a3b8" },
};

const PRIORITY_META = {
  urgent: { label: "Urgent", color: "#ef4444" },
  high:   { label: "High",   color: "#f97316" },
  medium: { label: "Medium", color: "#eab308" },
  normal: { label: "Normal", color: "#3b82f6" },
  low:    { label: "Low",    color: "#22c55e" },
};

function TasksView({ tasks, onOpen, onDrop, onAdd, showProject, onOpenChatbot }) {
  const [view, setView] = useState("board");           // "list" | "board"
  const [filter, setFilter] = useState("all");         // all | open | in_progress | completed
  const [q, setQ] = useState("");                      // search query

  const filtered = useMemo(() => {
    const isClosed = (t) => t.status === "done" || t.status === "completed";
    const isOpen   = (t) => !isClosed(t);
    let out;
    switch (filter) {
      case "all":         out = tasks; break;
      case "open":        out = tasks.filter(isOpen); break;
      case "in_progress": out = tasks.filter(t => t.status === "in_progress" || t.status === "doing"); break;
      case "completed":   out = tasks.filter(isClosed); break;
      default:            out = tasks; break;
    }
    const query = q.trim().toLowerCase();
    if (!query) return out;
    return out.filter(t => {
      const hay = [
        t.title, t.description, t.owner, t.owner_name, t.project_name, t.project,
        ...(t.tags || []),
        ...((t.assignees || []).map(a => a.name || a.email || "")),
      ].join(" ").toLowerCase();
      return hay.includes(query);
    });
  }, [tasks, filter, q]);

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-white shrink-0">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="tasks-filter"
          className="text-sm font-semibold text-slate-800 bg-transparent border-none outline-none pr-4 cursor-pointer"
        >
          <option value="all">All Tasks</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </select>

        {/* Search input */}
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            data-testid="tasks-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tasks…"
            className="pl-7 pr-2 py-1 text-xs border border-slate-200 rounded outline-none focus:border-indigo-400 w-52 bg-white text-slate-700 placeholder:text-slate-400"
          />
        </div>

        {/* Ask AI button */}
        <button
          data-testid="tasks-ask-ai"
          onClick={() => onOpenChatbot?.()}
          className="text-xs font-semibold px-2.5 py-1 rounded border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 flex items-center gap-1"
        >
          <Bot size={12} /> Ask AI
        </button>

        <div className="flex-1" />
        <div className="flex items-center gap-0 border border-slate-200 rounded overflow-hidden">
          <button
            onClick={() => setView("list")}
            data-testid="tasks-view-list"
            className={`text-xs font-semibold px-2.5 py-1 flex items-center gap-1 transition ${view === "list" ? "bg-indigo-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            <ListChecks size={12} /> List
          </button>
          <button
            onClick={() => setView("board")}
            data-testid="tasks-view-board"
            className={`text-xs font-semibold px-2.5 py-1 flex items-center gap-1 transition ${view === "board" ? "bg-indigo-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            <LayoutDashboard size={12} /> Board
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto">
        {view === "board" ? (
          <KanbanBoard tasks={filtered} onOpen={onOpen} onDrop={onDrop} onAdd={onAdd} showProject={showProject} />
        ) : (
          <TasksTable tasks={filtered} onOpen={onOpen} showProject={showProject} />
        )}
      </div>
    </div>
  );
}

function TasksTable({ tasks, onOpen, showProject }) {
  const fmt = (raw) => {
    if (!raw) return "";
    const d = new Date(raw); if (isNaN(d.getTime())) return String(raw);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };
  const displayName = (raw) => {
    if (!raw) return "—";
    let s = String(raw).trim();
    if (s.startsWith("@")) s = s.slice(1);
    if (s.includes("@")) s = s.split("@")[0];
    if (s.toLowerCase() === "info") return "HEXA Admin";
    return s.split(/[._\-\s]+/).filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  };
  const initials = (n) => (n || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  const tint = (n) => { let h = 0; for (const c of (n || "?")) h = (h * 31 + c.charCodeAt(0)) % 360; return `hsl(${h} 70% 55%)`; };
  const statusMeta = (t) => TASK_STATUS_META[t.status] || TASK_STATUS_META.todo;
  const priMeta = (t) => PRIORITY_META[(t.priority || "normal").toLowerCase()] || PRIORITY_META.normal;

  if (tasks.length === 0) {
    return <div className="p-8 text-center text-sm text-slate-500">No tasks to show.</div>;
  }

  return (
    <table className="w-full text-sm border-collapse" data-testid="tasks-table">
      <thead>
        <tr className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200 min-w-[220px]">Task</th>
          <th className="text-left font-bold px-2 py-2.5 border-y border-slate-200 w-16"></th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Owner</th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Due</th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Status</th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Assignee</th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Priority</th>
          {showProject && <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Project</th>}
        </tr>
      </thead>
      <tbody>
        {tasks.map(t => {
          const owner = displayName(t.owner || t.owner_name);
          const assignee = displayName((t.assignees || [])[0]?.name || (t.assignees || [])[0]?.email || t.assignee || t.owner);
          const sm = statusMeta(t);
          const pm = priMeta(t);
          const attachCount = (t.attachments || []).length;
          const commentCount = (t.comments || []).length;
          return (
            <tr key={t.id} onClick={() => onOpen?.(t)} data-testid={`task-row-${t.id}`} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
              <td className="px-4 py-2.5 text-slate-800 font-medium">{t.title || "Untitled"}</td>
              <td className="px-2 py-2.5">
                <div className="flex items-center gap-1 text-slate-400">
                  {attachCount > 0 && <span title={`${attachCount} attachments`} className="inline-flex items-center gap-0.5 text-xs"><Paperclip size={11} />{attachCount > 1 ? attachCount : ""}</span>}
                  {commentCount > 0 && <span title={`${commentCount} comments`} className="inline-flex items-center gap-0.5 text-xs"><Clock size={11} /></span>}
                </div>
              </td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full text-white text-[9px] font-bold flex items-center justify-center shrink-0" style={{ background: tint(owner) }}>{initials(owner)}</div>
                  <span className="text-slate-700 text-xs font-medium truncate">{owner}</span>
                </div>
              </td>
              <td className="px-4 py-2.5 text-slate-600 text-xs whitespace-nowrap tabular-nums">{fmt(t.due || t.end_date)}</td>
              <td className="px-4 py-2.5">
                <span className="text-white text-[11px] font-bold px-3 py-1 rounded" style={{ background: sm.bg }}>{sm.label}</span>
              </td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full text-white text-[9px] font-bold flex items-center justify-center shrink-0" style={{ background: tint(assignee) }}>{initials(assignee)}</div>
                  <span className="text-slate-700 text-xs font-medium truncate">{assignee}</span>
                </div>
              </td>
              <td className="px-4 py-2.5">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: pm.color }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: pm.color }} />
                  {pm.label}
                </span>
              </td>
              {showProject && <td className="px-4 py-2.5 text-slate-600 text-xs truncate">{t.project_name || t.project || ""}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* ============================================================
   IssuesView — All Open list + Board views with filter, submit button.
   ============================================================ */

const ISSUE_STATUS_META = {
  open:        { label: "Open",         color: "#2563eb", bg: "#3b82f6" },
  todo:        { label: "To do",        color: "#22c55e", bg: "#22c55e" },
  to_do:       { label: "To do",        color: "#22c55e", bg: "#22c55e" },
  in_progress: { label: "In progress",  color: "#f59e0b", bg: "#f59e0b" },
  doing:       { label: "In progress",  color: "#f59e0b", bg: "#f59e0b" },
  reopen:      { label: "Reopen",       color: "#ef4444", bg: "#f87171" },
  on_hold:     { label: "On hold",      color: "#c026d3", bg: "#c026d3" },
  done:        { label: "Closed",       color: "#0ea5e9", bg: "#22d3ee" },
  completed:   { label: "Closed",       color: "#0ea5e9", bg: "#22d3ee" },
  backlog:     { label: "Backlog",      color: "#64748b", bg: "#94a3b8" },
};

const ISSUE_SEVERITY = ["Showstopper", "Major", "Medium", "Low", "None"];

function IssuesView({ issues, onOpen, onDrop, onAdd, showProject }) {
  const [view, setView] = useState("list");           // "list" | "board"
  const [filter, setFilter] = useState("all_open");   // all_open | all | reopen | in_progress | closed

  const filtered = useMemo(() => {
    const isClosed = (t) => t.status === "done" || t.status === "completed";
    const isOpen   = (t) => !isClosed(t);
    switch (filter) {
      case "all":         return issues;
      case "all_open":    return issues.filter(isOpen);
      case "reopen":      return issues.filter(t => t.status === "reopen");
      case "in_progress": return issues.filter(t => t.status === "in_progress" || t.status === "doing");
      case "closed":      return issues.filter(isClosed);
      default:            return issues;
    }
  }, [issues, filter]);

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-white shrink-0">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="issues-filter"
          className="text-sm font-semibold text-slate-800 bg-transparent border-none outline-none pr-4 cursor-pointer"
        >
          <option value="all_open">All Open</option>
          <option value="all">All Issues</option>
          <option value="reopen">Reopen</option>
          <option value="in_progress">In progress</option>
          <option value="closed">Closed</option>
        </select>
        <div className="flex-1" />
        <div className="flex items-center gap-0 border border-slate-200 rounded overflow-hidden">
          <button
            onClick={() => setView("list")}
            data-testid="issues-view-list"
            className={`text-xs font-semibold px-2.5 py-1 flex items-center gap-1 transition ${view === "list" ? "bg-indigo-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            <ListChecks size={12} /> List
          </button>
          <button
            onClick={() => setView("board")}
            data-testid="issues-view-board"
            className={`text-xs font-semibold px-2.5 py-1 flex items-center gap-1 transition ${view === "board" ? "bg-indigo-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            <LayoutDashboard size={12} /> Board
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto">
        {view === "board" ? (
          filtered.length > 0
            ? <KanbanBoard tasks={filtered} onOpen={onOpen} onDrop={onDrop} onAdd={onAdd} showProject={showProject} hideIssueChip />
            : <EmptyState icon={GitBranch} title="No issues match" body="Try changing the filter above, or create a new issue from the header." />
        ) : (
          <IssuesTable issues={filtered} onOpen={onOpen} showProject={showProject} />
        )}
      </div>
    </div>
  );
}

function IssuesTable({ issues, onOpen, showProject }) {
  const fmt = (raw) => {
    if (!raw) return "";
    const d = new Date(raw); if (isNaN(d.getTime())) return String(raw);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };
  const displayName = (raw) => {
    if (!raw) return "—";
    let s = String(raw).trim();
    if (s.startsWith("@")) s = s.slice(1);
    if (s.includes("@")) s = s.split("@")[0];
    if (s.toLowerCase() === "info") return "HEXA Admin";
    return s.split(/[._\-\s]+/).filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  };
  const initials = (n) => (n || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  const tint = (n) => { let h = 0; for (const c of (n || "?")) h = (h * 31 + c.charCodeAt(0)) % 360; return `hsl(${h} 70% 55%)`; };
  const severityOf = (t) => {
    const p = (t.priority || "").toLowerCase();
    if (p === "urgent") return "Showstopper";
    if (p === "high") return "Major";
    if (p === "medium") return "Medium";
    if (p === "low") return "Low";
    return "None";
  };
  const severityColor = (s) => ({
    Showstopper: "#ef4444", Major: "#f97316", Medium: "#eab308", Low: "#22c55e", None: "#94a3b8",
  }[s] || "#94a3b8");
  const statusMeta = (t) => ISSUE_STATUS_META[t.status] || ISSUE_STATUS_META.open;

  if (issues.length === 0) {
    return <div className="p-8 text-center text-sm text-slate-500">No issues to show.</div>;
  }

  return (
    <table className="w-full text-sm border-collapse" data-testid="issues-table">
      <thead>
        <tr className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200 min-w-[220px]">Issue</th>
          <th className="text-left font-bold px-2 py-2.5 border-y border-slate-200 w-16"></th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Reporter</th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Created</th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Status</th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Assignee</th>
          <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Severity</th>
          {showProject && <th className="text-left font-bold px-4 py-2.5 border-y border-slate-200">Project</th>}
        </tr>
      </thead>
      <tbody>
        {issues.map(t => {
          const reporter = displayName(t.owner);
          const assignee = displayName((t.assignees || [])[0]?.name || (t.assignees || [])[0]?.email || t.assignee || t.owner);
          const sev = severityOf(t);
          const sm = statusMeta(t);
          const attachCount = (t.attachments || []).length;
          const commentCount = (t.comments || []).length;
          return (
            <tr key={t.id} onClick={() => onOpen?.(t)} data-testid={`issue-row-${t.id}`} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
              <td className="px-4 py-2.5 text-slate-800 font-medium">{t.title || "Untitled"}</td>
              <td className="px-2 py-2.5">
                <div className="flex items-center gap-1 text-slate-400">
                  {attachCount > 0 && <span title={`${attachCount} attachments`} className="inline-flex items-center gap-0.5 text-xs"><Paperclip size={11} />{attachCount > 1 ? attachCount : ""}</span>}
                  {commentCount > 0 && <span title={`${commentCount} comments`} className="inline-flex items-center gap-0.5 text-xs"><Clock size={11} /></span>}
                </div>
              </td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full text-white text-[9px] font-bold flex items-center justify-center shrink-0" style={{ background: tint(reporter) }}>{initials(reporter)}</div>
                  <span className="text-slate-700 text-xs font-medium truncate">{reporter}</span>
                </div>
              </td>
              <td className="px-4 py-2.5 text-slate-600 text-xs whitespace-nowrap tabular-nums">{fmt(t.created)}</td>
              <td className="px-4 py-2.5">
                <span className="text-white text-[11px] font-bold px-3 py-1 rounded" style={{ background: sm.bg }}>{sm.label}</span>
              </td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full text-white text-[9px] font-bold flex items-center justify-center shrink-0" style={{ background: tint(assignee) }}>{initials(assignee)}</div>
                  <span className="text-slate-700 text-xs font-medium truncate">{assignee}</span>
                </div>
              </td>
              <td className="px-4 py-2.5">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: severityColor(sev) }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: severityColor(sev) }} />
                  {sev}
                </span>
              </td>
              {showProject && <td className="px-4 py-2.5 text-slate-600 text-xs truncate">{t.project_name || t.project || ""}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SubmitIssueModal({ onClose, onCreated, allTasks, defaultProject = "" }) {
  const [form, setForm] = useState({ title: "", description: "", severity: "Medium", project: defaultProject || "" });
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);
  const projects = useMemo(() => Array.from(new Set(allTasks.map(t => t.project_name || t.project).filter(Boolean))), [allTasks]);

  const uploadDesktop = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Max 5MB per file"); return; }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
    });
    setAttachments(prev => [
      ...prev,
      { id: `local-${Date.now()}`, name: file.name, mime: file.type, size: file.size, data: b64 },
    ]);
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      const priorityMap = { Showstopper: "urgent", Major: "high", Medium: "medium", Low: "low", None: "low" };
      await api.post("/tasks", {
        title: form.title.trim(),
        description: form.description,
        priority: priorityMap[form.severity] || "high",
        status: "todo",
        tags: ["issue"],
        project: form.project || "Inbox",
        attachments,
      });
      onCreated?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create issue");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[500] bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="hexa-force-light hexa-form-colorful w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden" data-testid="issue-submit-modal">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-white">
          <div className="text-sm font-semibold flex items-center gap-2 text-slate-800"><GitBranch className="text-indigo-500" size={14} /> Create Issue</div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3 bg-white">
          <div>
            <div className="field-label text-[10px] uppercase tracking-widest text-slate-500 mb-1">Issue title *</div>
            <input autoFocus value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short summary of the problem" className="w-full text-sm px-2 py-1.5 rounded outline-none" data-testid="issue-submit-title" />
          </div>
          <div>
            <div className="field-label text-[10px] uppercase tracking-widest text-slate-500 mb-1">Description</div>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Steps to reproduce, expected vs actual, screenshots…" className="w-full text-sm px-2 py-1.5 rounded outline-none resize-y" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="field-label text-[10px] uppercase tracking-widest text-slate-500 mb-1">Severity</div>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded outline-none bg-white">
                {ISSUE_SEVERITY.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div className="field-label text-[10px] uppercase tracking-widest text-slate-500 mb-1">Project</div>
              <select value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} className="w-full text-sm px-2 py-1.5 rounded outline-none bg-white">
                <option value="">Inbox</option>
                {projects.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="field-label text-[10px] uppercase tracking-widest text-slate-500">Attachment</div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="issue-submit-attach-btn"
                className="text-[11px] font-semibold px-2 py-1 rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 flex items-center gap-1 text-indigo-700"
              >
                <Paperclip size={11} /> Attach file
              </button>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                data-testid="issue-submit-attach-input"
                onChange={(e) => { uploadDesktop(e.target.files?.[0]); e.target.value = ""; }}
              />
            </div>
            {attachments.length === 0 ? (
              <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 rounded p-2 text-center">
                No attachments — add screenshots, logs, or reference files (max 5MB).
              </div>
            ) : (
              <div className="space-y-1">
                {attachments.map(a => (
                  <div key={a.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded bg-white">
                    <div className="w-7 h-7 bg-indigo-50 text-indigo-600 rounded flex items-center justify-center"><Paperclip size={11} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold truncate text-slate-700">{a.name}</div>
                      <div className="text-[10px] text-slate-500">{Math.round((a.size || 0) / 1024)} KB</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAttachments(prev => prev.filter(x => x.id !== a.id))}
                      data-testid={`issue-submit-attach-remove-${a.id}`}
                      className="p-1 hover:bg-slate-100 rounded text-red-500"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded hover:bg-slate-200 text-slate-700">Cancel</button>
          <button onClick={submit} disabled={busy || !form.title.trim()} className="text-sm px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-1" data-testid="issue-submit-confirm">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectUsers({ tasks }) {
  const map = new Map();
  tasks.forEach(t => (t.assignees || []).forEach(a => {
    const k = a.name || a.initials || "?";
    if (!map.has(k)) map.set(k, { ...a, tasks: 0, completed: 0 });
    const rec = map.get(k);
    rec.tasks += 1;
    if (t.status === "done" || t.status === "completed") rec.completed += 1;
  }));
  const rows = Array.from(map.values()).sort((a, b) => b.tasks - a.tasks);
  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="max-w-4xl">
        <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500 mono mb-3">Team on this project</div>
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm divide-y divide-slate-100">
          {rows.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No members yet — add tasks with assignees to see team here.</div>}
          {rows.map((r, i) => (
            <div key={i} className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs"
                   style={{ background: r.color || avatarColorFor(r.name || r.initials || "?") }}>
                {r.initials || initialsFor(r.name || "?")}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800">{r.name}</div>
                <div className="text-xs text-slate-500">{r.tasks} tasks · {r.completed} completed</div>
              </div>
              <div className="w-24 h-1.5 bg-slate-200 rounded overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${r.tasks ? Math.round((r.completed / r.tasks) * 100) : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HomeOverview({ projects, user, onNewProject, onOpenProject }) {
  const active = projects.filter(p => p.status === "active").length;
  const done = projects.filter(p => p.status === "done").length;
  return (
    <div className="p-8 overflow-y-auto h-full">
      <div className="max-w-5xl">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500 mono">Welcome back</div>
            <h1 className="text-2xl font-bold text-slate-800 mt-1">{(user?.name || user?.address || "").split("@")[0]}</h1>
            <p className="text-slate-500 text-sm mt-2">Your unified workspace for projects, tasks, phases and team coordination.</p>
          </div>
          <button onClick={onNewProject} data-testid="proj-home-new"
            className="text-xs font-semibold px-4 py-2 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-sm flex items-center gap-1.5">
            <Plus size={13} /> New Project
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          {[
            { label: "Total Projects", value: projects.length, tint: "#6366f1" },
            { label: "Active",         value: active,          tint: "#22c55e" },
            { label: "Completed",      value: done,            tint: "#3b82f6" },
            { label: "On Hold",        value: projects.filter(p => p.status === "on_hold").length, tint: "#94a3b8" },
          ].map(k => (
            <div key={k.label} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mono">{k.label}</div>
              <div className="text-2xl font-bold mt-2" style={{ color: k.tint }}>{k.value}</div>
            </div>
          ))}
        </div>

        {projects.length > 0 && (
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500 mono mb-2">Recent Projects</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.slice(0, 6).map(p => (
                <button key={p.id} onClick={() => onOpenProject?.(p.id)}
                  className="text-left bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition">
                  <div className="text-[10px] mono uppercase tracking-widest text-indigo-600">{p.code || `PR-${(p.id || "").slice(0,3).toUpperCase()}`}</div>
                  <div className="text-sm font-semibold text-slate-800 mt-1 line-clamp-2">{p.title || p.name}</div>
                  <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_DOT[p.status] || "#94a3b8" }} />
                    {p.status || "active"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {projects.length === 0 && (
          <div className="mt-8 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg p-8 text-center">
            <div className="text-slate-800 font-semibold">No projects yet</div>
            <div className="text-slate-500 text-sm mt-1">Create your first project to start organising tasks, team and finance in one place.</div>
            <button onClick={onNewProject}
              className="mt-4 text-xs font-semibold px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white inline-flex items-center gap-1.5">
              <Plus size={13} /> Create your first project
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="max-w-md text-center px-6">
        <div className="w-14 h-14 rounded-full bg-indigo-50 text-indigo-500 flex items-center justify-center mx-auto"><Icon size={22} /></div>
        <h2 className="text-lg font-bold text-slate-800 mt-4">{title}</h2>
        <p className="text-sm text-slate-500 mt-2">{body}</p>
      </div>
    </div>
  );
}

function NavGroup({ title, children }) {
  return (
    <div className="pt-3 pb-1">
      {title && <div className="px-4 mb-1 text-[9px] uppercase tracking-[0.28em] text-slate-400 mono font-semibold">{title}</div>}
      <div className="px-2 space-y-0.5">{children}</div>
    </div>
  );
}

function NavItem({ icon: Icon, label, count, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`w-full text-left px-3 py-1.5 rounded flex items-center gap-2 text-xs transition ${active ? "bg-indigo-600 text-white font-semibold shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}>
      <Icon size={13} />
      <span className="flex-1">{label}</span>
      {typeof count === "number" && (
        <span className={`text-[10px] px-1.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{count}</span>
      )}
    </button>
  );
}

const STATUS_DOT = {
  active:   "#22c55e",
  pipeline: "#f59e0b",
  on_hold:  "#94a3b8",
  done:     "#3b82f6",
};

// A project is "new" only if it was created today (local timezone).
function isCreatedToday(raw) {
  if (!raw) return false;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
      && d.getMonth()    === now.getMonth()
      && d.getDate()     === now.getDate();
}

/* ============================================================
   Ready-made project templates — Sales / Marketing / IT / HR.
   Each seeds a starter task list so a new project is instantly
   populated with department-specific work.
   ============================================================ */
export const PROJECT_TEMPLATES = {
  blank: {
    key: "blank",
    label: "Blank",
    department: "",
    icon: "📄",
    color: "#64748b",
    description: "",
    tasks: [],
  },
  sales: {
    key: "sales",
    label: "Sales",
    department: "sales",
    icon: "💼",
    color: "#22c55e",
    description: "Standard sales pipeline — leads → qualification → proposal → close.",
    tasks: [
      { title: "Build target account list",         status: "todo",        priority: "high",   tags: ["sales", "prospecting"], category: "PROSPECTING" },
      { title: "Run outbound outreach campaign",    status: "todo",        priority: "high",   tags: ["sales", "outreach"],    category: "OUTREACH" },
      { title: "Qualify inbound leads (BANT)",      status: "todo",        priority: "medium", tags: ["sales", "qualify"],     category: "QUALIFICATION" },
      { title: "Prepare discovery deck",            status: "todo",        priority: "medium", tags: ["sales", "collateral"],  category: "COLLATERAL" },
      { title: "Send proposal & pricing",           status: "in_progress", priority: "high",   tags: ["sales", "proposal"],    category: "PROPOSAL" },
      { title: "Contract negotiation",              status: "todo",        priority: "high",   tags: ["sales", "negotiation"], category: "NEGOTIATION" },
      { title: "Kickoff onboarding call",           status: "todo",        priority: "medium", tags: ["sales", "onboarding"],  category: "CLOSE" },
    ],
  },
  marketing: {
    key: "marketing",
    label: "Marketing",
    department: "marketing",
    icon: "📣",
    color: "#ec4899",
    description: "Campaign planning, content, distribution and performance measurement.",
    tasks: [
      { title: "Define campaign goals & KPIs",      status: "todo", priority: "high",   tags: ["marketing", "plan"],     category: "PLANNING" },
      { title: "Persona & audience research",       status: "todo", priority: "medium", tags: ["marketing", "research"], category: "RESEARCH" },
      { title: "Content calendar (4 weeks)",        status: "todo", priority: "high",   tags: ["marketing", "content"],  category: "CONTENT" },
      { title: "Landing page copy + design",        status: "todo", priority: "high",   tags: ["marketing", "web"],      category: "WEB" },
      { title: "Email sequence & automation",       status: "todo", priority: "medium", tags: ["marketing", "email"],    category: "EMAIL" },
      { title: "Paid social ads (LinkedIn / Meta)", status: "todo", priority: "medium", tags: ["marketing", "ads"],      category: "PAID" },
      { title: "Weekly performance report",         status: "todo", priority: "low",    tags: ["marketing", "report"],   category: "ANALYTICS" },
    ],
  },
  it: {
    key: "it",
    label: "IT / Engineering",
    department: "it",
    icon: "💻",
    color: "#0ea5e9",
    description: "Technical delivery — architecture, infra, dev, QA and release.",
    tasks: [
      { title: "Technical requirements & scope",    status: "todo",        priority: "high",   tags: ["it", "spec"],   category: "DISCOVERY" },
      { title: "Architecture & tech stack decision", status: "todo",       priority: "high",   tags: ["it", "arch"],   category: "ARCHITECTURE" },
      { title: "Environment & CI/CD setup",         status: "todo",        priority: "high",   tags: ["it", "devops"], category: "INFRA" },
      { title: "Backend API implementation",        status: "in_progress", priority: "high",   tags: ["it", "backend"], category: "DEV" },
      { title: "Frontend implementation",           status: "in_progress", priority: "high",   tags: ["it", "frontend"], category: "DEV" },
      { title: "Automated tests (unit + e2e)",      status: "todo",        priority: "medium", tags: ["it", "qa"],     category: "TESTING" },
      { title: "Security review & penetration test", status: "todo",       priority: "high",   tags: ["it", "security"], category: "SECURITY" },
      { title: "Production release & monitoring",   status: "todo",        priority: "urgent", tags: ["it", "release"], category: "RELEASE" },
    ],
  },
  hr: {
    key: "hr",
    label: "HR",
    department: "hr",
    icon: "👥",
    color: "#f59e0b",
    description: "Hiring, onboarding, performance and people ops workflows.",
    tasks: [
      { title: "Publish job description(s)",        status: "todo", priority: "high",   tags: ["hr", "hiring"],       category: "HIRING" },
      { title: "Source & screen candidates",        status: "todo", priority: "high",   tags: ["hr", "recruiting"],   category: "HIRING" },
      { title: "Interview loop coordination",       status: "todo", priority: "medium", tags: ["hr", "interviews"],   category: "HIRING" },
      { title: "Reference & background checks",     status: "todo", priority: "medium", tags: ["hr", "compliance"],   category: "COMPLIANCE" },
      { title: "Offer letter & contract",           status: "todo", priority: "high",   tags: ["hr", "offer"],        category: "HIRING" },
      { title: "Onboarding plan (30-60-90 days)",   status: "todo", priority: "high",   tags: ["hr", "onboarding"],   category: "ONBOARDING" },
      { title: "Quarterly performance reviews",     status: "todo", priority: "medium", tags: ["hr", "performance"],  category: "PERFORMANCE" },
      { title: "Employee engagement pulse survey",  status: "todo", priority: "low",    tags: ["hr", "engagement"],   category: "ENGAGEMENT" },
    ],
  },
  finance: {
    key: "finance",
    label: "Finance",
    department: "finance",
    icon: "💰",
    color: "#10b981",
    description: "Accounts, invoicing, payments, GST, payroll and financial reporting.",
    tasks: [
      { title: "Prepare monthly P&L statement",         status: "todo",        priority: "high",   tags: ["finance", "reporting"],  category: "REPORTING" },
      { title: "Reconcile bank & payment gateways",     status: "in_progress", priority: "high",   tags: ["finance", "reconcile"],  category: "RECONCILIATION" },
      { title: "Issue client invoices for the month",   status: "todo",        priority: "high",   tags: ["finance", "invoicing"],  category: "INVOICING" },
      { title: "Track vendor bills & pending payments", status: "todo",        priority: "medium", tags: ["finance", "vendors"],    category: "PAYABLES" },
      { title: "Process payroll & salary disbursement", status: "todo",        priority: "urgent", tags: ["finance", "payroll"],    category: "PAYROLL" },
      { title: "File GST / tax returns",                status: "todo",        priority: "high",   tags: ["finance", "gst", "tax"], category: "COMPLIANCE" },
      { title: "Update expense tracker & approvals",    status: "todo",        priority: "medium", tags: ["finance", "expense"],    category: "EXPENSE" },
      { title: "Prepare quarterly budget forecast",     status: "todo",        priority: "medium", tags: ["finance", "budget"],     category: "BUDGET" },
      { title: "Audit trail & compliance review",       status: "todo",        priority: "low",    tags: ["finance", "audit"],      category: "AUDIT" },
    ],
  },
};

export const DEPARTMENTS = [
  { key: "sales",     label: "Sales",     color: "#22c55e", icon: "💼" },
  { key: "marketing", label: "Marketing", color: "#ec4899", icon: "📣" },
  { key: "it",        label: "IT",        color: "#0ea5e9", icon: "💻" },
  { key: "hr",        label: "HR",        color: "#f59e0b", icon: "👥" },
  { key: "finance",   label: "Finance",   color: "#10b981", icon: "💰" },
];

/* ============================================================
   Global views for sidebar nav items — Phases / Time Logs /
   Timesheets / Expenses / Vendors. Data-backed where a source
   is already available (Phases + Time Logs read from /api/tasks).
   ============================================================ */

function PhasesView({ projectFilter = "" }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/tasks"); setTasks(data || []); }
    catch { setTasks([]); }
    setLoading(false);
  })(); }, []);

  // Group by category_tag (fallback = "UNCATEGORIZED")
  const groups = useMemo(() => {
    const scoped = projectFilter ? tasks.filter(t => t.project === projectFilter) : tasks;
    const g = {};
    scoped.forEach(t => {
      const key = (t.category_tag || "UNCATEGORIZED").toUpperCase();
      (g[key] = g[key] || []).push(t);
    });
    return Object.entries(g).sort((a, b) => b[1].length - a[1].length);
  }, [tasks, projectFilter]);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50 p-6">
      <PageHeader icon={Layers} title="Phases" subtitle="Task distribution across phases / categories" />
      {loading ? <LoadingRow /> : groups.length === 0 ? (
        <EmptyState icon={Layers} title="No phases yet" body="Tag your tasks with a category (BACK LOG, DESIGN, TESTING, DEV, …) and they'll appear here grouped by phase." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(([label, items]) => (
            <div key={label} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid={`phase-${label.toLowerCase()}`}>
              <div className="px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white flex items-center justify-between">
                <div className="text-sm font-bold tracking-wide">{label}</div>
                <div className="text-[11px] bg-white/25 rounded-full px-2 py-0.5 font-semibold">{items.length}</div>
              </div>
              <div className="p-3 space-y-2 max-h-72 overflow-y-auto">
                {items.slice(0, 20).map(t => (
                  <div key={t.id} className="flex items-center gap-2 text-xs text-slate-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                    <span className="truncate">{t.title || "Untitled"}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimeLogsView({ projectFilter = "" }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/tasks"); setTasks(data || []); }
    catch { setTasks([]); }
    setLoading(false);
  })(); }, []);

  const logged = useMemo(() => {
    const scoped = projectFilter ? tasks.filter(t => t.project === projectFilter) : tasks;
    return scoped
      .filter(t => (t.work_hours || 0) > 0)
      .sort((a, b) => (b.work_hours || 0) - (a.work_hours || 0));
  }, [tasks, projectFilter]);
  const totalHours = logged.reduce((s, t) => s + (t.work_hours || 0), 0);

  const assigneeLabel = (t) => {
    const list = Array.isArray(t.assignees) ? t.assignees : [];
    const names = list.map(a => (a && (a.name || a.address)) || "").filter(Boolean);
    if (!names.length) return "—";
    if (names.length === 1) return shortName(names[0]);
    return `${shortName(names[0])} +${names.length - 1}`;
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50 p-6">
      <PageHeader icon={Clock} title="Time Logs" subtitle="Hours logged per task" />
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatCard label="Tasks Tracked" value={logged.length} color="#6366f1" />
        <StatCard label="Total Hours"   value={totalHours.toFixed(1)} color="#0ea5e9" />
        <StatCard label="Avg per Task"  value={logged.length ? (totalHours / logged.length).toFixed(1) : "0"} color="#22c55e" />
      </div>
      {loading ? <LoadingRow /> : logged.length === 0 ? (
        <EmptyState icon={Clock} title="No time logged" body="Open a task and set Work Hours to see it here." />
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="timelogs-table">
          <div className="grid grid-cols-12 px-4 py-2 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-600">
            <div className="col-span-2">Task Code</div>
            <div className="col-span-2">Project</div>
            <div className="col-span-3">Task Name</div>
            <div className="col-span-2">Owner</div>
            <div className="col-span-2">Assignee</div>
            <div className="col-span-1 text-right">Hours</div>
          </div>
          {logged.map(t => (
            <div key={t.id} className="grid grid-cols-12 px-4 py-2 border-t border-slate-100 text-xs text-slate-700 hover:bg-slate-50" data-testid={`timelog-row-${t.id}`}>
              <div className="col-span-2 font-mono text-slate-500">{t.task_code || "—"}</div>
              <div className="col-span-2 truncate text-slate-600">{t.project || "—"}</div>
              <div className="col-span-3 truncate">{t.title || "Untitled"}</div>
              <div className="col-span-2 truncate text-slate-500">{shortName(t.owner_name || t.owner)}</div>
              <div className="col-span-2 truncate text-slate-500">{assigneeLabel(t)}</div>
              <div className="col-span-1 text-right font-semibold text-indigo-600">{(t.work_hours || 0).toFixed(1)}h</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimesheetsView({ projectFilter = "" }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/tasks"); setTasks(data || []); }
    catch { setTasks([]); }
    setLoading(false);
  })(); }, []);

  // Group by owner and sum hours
  const byOwner = useMemo(() => {
    const scoped = projectFilter ? tasks.filter(t => t.project === projectFilter) : tasks;
    const m = {};
    scoped.forEach(t => {
      const owner = shortName(t.owner_name || t.owner);
      const hrs = t.work_hours || 0;
      if (!hrs) return;
      m[owner] = (m[owner] || 0) + hrs;
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [tasks, projectFilter]);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50 p-6">
      <PageHeader icon={Timer} title="Timesheets" subtitle="Total hours per team member" />
      {loading ? <LoadingRow /> : byOwner.length === 0 ? (
        <EmptyState icon={Timer} title="No timesheet data" body="As team members log work hours on their tasks, aggregated totals will show up here." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {byOwner.map(([owner, hrs]) => (
            <div key={owner} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white grid place-items-center font-bold text-sm">
                {initialsFor(owner)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{owner}</div>
                <div className="text-[11px] text-slate-500">This period</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-indigo-600">{hrs.toFixed(1)}h</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExpensesView({ projectFilter = "" }) {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ project: projectFilter || "", vendor: "", status: "" });

  useEffect(() => {
    setFilter(f => ({ ...f, project: projectFilter || "" }));
  }, [projectFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const [ex, vd, pr] = await Promise.all([
        api.get("/expenses"),
        api.get("/vendors"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setRows(ex.data || []);
      setVendors(vd.data || []);
      setProjects(pr.data || []);
    } catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(r => {
    if (filter.project && r.project !== filter.project) return false;
    if (filter.vendor  && r.vendor_id !== filter.vendor) return false;
    if (filter.status  && r.status !== filter.status)   return false;
    return true;
  }), [rows, filter]);

  const total = filtered.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const pending = filtered.filter(r => r.status === "pending").reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const paid    = filtered.filter(r => r.status === "paid").reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const openNew = () => { setEditing({ description: "", amount: "", currency: "INR", category: "", date: new Date().toISOString().slice(0, 10), project: projectFilter || "", vendor_id: "", vendor_name: "", status: "pending", payment_method: "", notes: "" }); setShowForm(true); };
  const openEdit = (r) => { setEditing({ ...r }); setShowForm(true); };
  const save = async () => {
    const payload = { ...editing, amount: parseFloat(editing.amount) || 0 };
    if (editing.vendor_id) {
      const v = vendors.find(x => x.id === editing.vendor_id);
      if (v) payload.vendor_name = v.name;
    } else {
      payload.vendor_name = "";
    }
    try {
      if (editing.id) await api.patch(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      setShowForm(false); setEditing(null);
      await load();
    } catch (e) { console.error(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); await load(); } catch (e) { console.error(e); }
  };

  const STATUS_COLORS = { pending: "#f59e0b", approved: "#0ea5e9", paid: "#22c55e", rejected: "#ef4444" };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50 p-6" data-testid="expenses-view">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Wallet size={18} className="text-emerald-500" />
            <h1 className="text-xl font-bold text-slate-800">Expenses</h1>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Track project spend, receipts and vendor invoices</div>
        </div>
        <button
          data-testid="expense-new-btn"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-sm"
        >
          <Plus size={14} /> New Expense
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatCard label="Total Spend" value={`$${total.toFixed(2)}`} color="#0ea5e9" />
        <StatCard label="Pending"     value={`$${pending.toFixed(2)}`} color="#f59e0b" />
        <StatCard label="Paid"        value={`$${paid.toFixed(2)}`}    color="#22c55e" />
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <select value={filter.project} onChange={e => setFilter({ ...filter, project: e.target.value })} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white" data-testid="expense-filter-project">
          <option value="">All projects</option>
          {projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
        </select>
        <select value={filter.vendor} onChange={e => setFilter({ ...filter, vendor: e.target.value })} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white" data-testid="expense-filter-vendor">
          <option value="">All vendors</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white" data-testid="expense-filter-status">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? <LoadingRow /> : filtered.length === 0 ? (
        <EmptyState icon={Wallet} title="No expenses yet" body="Log your first expense using the New Expense button." />
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="expenses-table">
          <div className="grid grid-cols-12 px-4 py-2 bg-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-600">
            <div className="col-span-2">Date</div>
            <div className="col-span-3">Description</div>
            <div className="col-span-2">Project</div>
            <div className="col-span-2">Vendor</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right">Amount</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {filtered.map(r => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-t border-slate-100 text-xs text-slate-700 hover:bg-slate-50 items-center" data-testid={`expense-row-${r.id}`}>
              <div className="col-span-2 text-slate-500">{r.date || "—"}</div>
              <div className="col-span-3 truncate">{r.description}</div>
              <div className="col-span-2 truncate text-slate-500">{r.project || "—"}</div>
              <div className="col-span-2 truncate text-slate-500">{r.vendor_name || "—"}</div>
              <div className="col-span-1">
                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider" style={{ background: `${STATUS_COLORS[r.status] || "#94a3b8"}22`, color: STATUS_COLORS[r.status] || "#64748b" }}>{r.status}</span>
              </div>
              <div className="col-span-1 text-right font-semibold text-slate-800">{(r.currency || "INR")} {Number(r.amount || 0).toFixed(2)}</div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-indigo-600 p-1" data-testid={`expense-edit-${r.id}`} title="Edit"><Pencil size={13} /></button>
                <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`expense-del-${r.id}`} title="Delete"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && editing && (
        <div className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4" onClick={() => { setShowForm(false); setEditing(null); }}>
          <div className="hexa-force-light hexa-form-colorful bg-white rounded-xl shadow-2xl w-[560px] max-w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="expense-form">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="text-sm font-bold text-slate-800">{editing.id ? "Edit Expense" : "New Expense"}</div>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <FormField label="Description">
                <input value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. AWS invoice - January" data-testid="expense-input-description" />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Amount">
                  <input type="number" step="0.01" value={editing.amount} onChange={e => setEditing({ ...editing, amount: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="expense-input-amount" />
                </FormField>
                <FormField label="Currency">
                  <select value={editing.currency} onChange={e => setEditing({ ...editing, currency: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                    {["INR","USD","EUR","GBP","AED","SGD","JPY"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Date">
                  <input type="date" value={editing.date} onChange={e => setEditing({ ...editing, date: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                </FormField>
                <FormField label="Category">
                  <select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <option value="">—</option>
                    {["Software","Travel","Marketing","Vendor Invoice","Office","Payroll","Legal","Other"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Project">
                  <select value={editing.project} onChange={e => setEditing({ ...editing, project: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <option value="">—</option>
                    {projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Vendor">
                  <select value={editing.vendor_id} onChange={e => setEditing({ ...editing, vendor_id: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <option value="">—</option>
                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Status">
                  <select value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="paid">Paid</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </FormField>
                <FormField label="Payment method">
                  <input value={editing.payment_method} onChange={e => setEditing({ ...editing, payment_method: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="Card, Wire, ACH…" />
                </FormField>
              </div>
              <FormField label="Notes">
                <textarea value={editing.notes} onChange={e => setEditing({ ...editing, notes: e.target.value })} rows={3} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </FormField>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-2 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={save} disabled={!editing.description || !editing.amount} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="expense-save-btn">
                <CheckIcon size={13} /> {editing.id ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VendorsView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/vendors"); setRows(data || []); }
    catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r =>
      (r.name || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s) ||
      (r.category || "").toLowerCase().includes(s));
  }, [rows, search]);

  const openNew = () => { setEditing({ name: "", contact_person: "", email: "", phone: "", category: "", payment_terms: "", website: "", address: "", notes: "", status: "active" }); setShowForm(true); };
  const openEdit = (v) => { setEditing({ ...v }); setShowForm(true); };
  const save = async () => {
    try {
      if (editing.id) await api.patch(`/vendors/${editing.id}`, editing);
      else await api.post("/vendors", editing);
      setShowForm(false); setEditing(null);
      await load();
    } catch (e) { console.error(e); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try { await api.delete(`/vendors/${id}`); await load(); } catch (e) { console.error(e); }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50 p-6" data-testid="vendors-view">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Truck size={18} className="text-amber-500" />
            <h1 className="text-xl font-bold text-slate-800">Vendors</h1>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Suppliers, contractors and their contract terms</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors" className="text-xs border border-slate-200 rounded-md pl-7 pr-2 py-2 bg-white w-56" data-testid="vendor-search" />
          </div>
          <button
            data-testid="vendor-new-btn"
            onClick={openNew}
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-sm"
          >
            <Plus size={14} /> New Vendor
          </button>
        </div>
      </div>

      {loading ? <LoadingRow /> : filtered.length === 0 ? (
        <EmptyState icon={Truck} title="No vendors yet" body="Add your first vendor to start tracking suppliers, contracts and invoices." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(v => (
            <div key={v.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-testid={`vendor-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white grid place-items-center font-bold text-sm">{initialsFor(v.name)}</div>
                  <div>
                    <div className="text-sm font-bold text-slate-800">{v.name}</div>
                    <div className="text-[11px] text-slate-500">{v.category || "Uncategorized"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(v)} className="text-slate-400 hover:text-indigo-600 p-1" data-testid={`vendor-edit-${v.id}`}><Pencil size={13} /></button>
                  <button onClick={() => del(v.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`vendor-del-${v.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-[12px] text-slate-600">
                {v.contact_person && <div>👤 {v.contact_person}</div>}
                {v.email && <div className="truncate">✉️ {v.email}</div>}
                {v.phone && <div>📞 {v.phone}</div>}
                {v.payment_terms && <div>💳 {v.payment_terms}</div>}
              </div>
              {v.status && (
                <div className="mt-3">
                  <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold"
                        style={{ background: v.status === "active" ? "#22c55e22" : "#94a3b822", color: v.status === "active" ? "#16a34a" : "#64748b" }}>
                    {v.status}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && editing && (
        <div className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4" onClick={() => { setShowForm(false); setEditing(null); }}>
          <div className="hexa-force-light hexa-form-colorful bg-white rounded-xl shadow-2xl w-[560px] max-w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="vendor-form">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="text-sm font-bold text-slate-800">{editing.id ? "Edit Vendor" : "New Vendor"}</div>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <FormField label="Vendor name">
                <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="Acme Supplies Ltd" data-testid="vendor-input-name" />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Contact person">
                  <input value={editing.contact_person} onChange={e => setEditing({ ...editing, contact_person: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                </FormField>
                <FormField label="Category">
                  <select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                    <option value="">—</option>
                    {["Software","Hardware","Marketing","Travel","Consulting","Legal","Logistics","Office","Other"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Email">
                  <input value={editing.email} onChange={e => setEditing({ ...editing, email: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="billing@vendor.com" />
                </FormField>
                <FormField label="Phone">
                  <input value={editing.phone} onChange={e => setEditing({ ...editing, phone: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Payment terms">
                  <input value={editing.payment_terms} onChange={e => setEditing({ ...editing, payment_terms: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="Net 30" />
                </FormField>
                <FormField label="Website">
                  <input value={editing.website} onChange={e => setEditing({ ...editing, website: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder="https://…" />
                </FormField>
              </div>
              <FormField label="Address">
                <input value={editing.address} onChange={e => setEditing({ ...editing, address: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </FormField>
              <FormField label="Status">
                <select value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </FormField>
              <FormField label="Notes">
                <textarea value={editing.notes} onChange={e => setEditing({ ...editing, notes: e.target.value })} rows={3} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              </FormField>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-2 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={save} disabled={!editing.name} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-50" data-testid="vendor-save-btn">
                <CheckIcon size={13} /> {editing.id ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <div className="field-label text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}

function PageHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-indigo-500" />
        <h1 className="text-xl font-bold text-slate-800">{title}</h1>
      </div>
      {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-extrabold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function LoadingRow() {
  return <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 size={12} className="animate-spin" /> Loading…</div>;
}

function shortName(raw) {
  if (!raw) return "";
  let s = String(raw).trim();
  if (s.startsWith("@")) s = s.slice(1);
  if (s.includes("@")) s = s.split("@")[0];
  if (s.toLowerCase() === "info") return "HEXA Admin";
  return s.split(/[._\-\s]+/).filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}
